/**
 * Generated avatars for entities and nodes (boring-avatars).
 *
 * Every entity shows a picture: the one an editor uploaded (`image_url`,
 * behind feature_real_pictures) or, failing that, one generated from the
 * entity's identity. Generated avatars are deterministic — same seed, same
 * palette, same picture on every device and every node — so they behave like
 * a real picture rather than a random placeholder.
 *
 * Editors can tweak the generated picture without uploading anything: the
 * entity's `avatar` field ({ seed, colors }) overrides the defaults below.
 * "Shuffle shape" stores a fresh seed, "shuffle colours" a fresh palette;
 * both are saved through the ordinary entity update.
 *
 * Variant per type is a product choice: pixel for apps, beam for people,
 * sunset for relationships, ring for contexts; marble for nodes, which are
 * not entities but need a face for federation (NodeAvatar.jsx).
 */

export const AVATAR_VARIANTS = {
    app: 'pixel',
    relationship: 'sunset',
    person: 'beam',
    context: 'ring',
};

export const NODE_AVATAR_VARIANT = 'marble';

export function avatarVariant(entityType) {
    // Unknown type: bauhaus, so it can't be mistaken for a node (marble).
    return AVATAR_VARIANTS[entityType] || 'bauhaus';
}

/**
 * Seed for an entity's generated avatar: the stored one if an editor shuffled
 * the shape, else the id (not the name, so a rename keeps the picture; the
 * origin id for a federated shadow row, so the same entity looks the same on
 * its home server and on a peer).
 */
export function avatarSeed(entity) {
    if (!entity) return 'entity';
    if (entity.avatar?.seed) return entity.avatar.seed;
    return `${entity.entity_type || 'entity'}:${entity.remote_id || entity.id}`;
}

// The stored palette, or undefined for the library's default one.
export function avatarColors(entity) {
    const colors = entity?.avatar?.colors;
    return Array.isArray(colors) && colors.length === 5 ? colors : undefined;
}

export function nodeAvatarSeed(serverId) {
    return `node:${serverId || 'local'}`;
}

// ── Shuffling ──────────────────────────────────────────────────────────────

const SEED_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

// A new shape: any string the entity has not had before will do.
export function randomSeed() {
    let s = '';
    for (let i = 0; i < 12; i++) s += SEED_ALPHABET[Math.floor(Math.random() * SEED_ALPHABET.length)];
    return s;
}

function hslToHex(h, s, l) {
    const sat = s / 100, light = l / 100;
    const k = n => (n + h / 30) % 12;
    const a = sat * Math.min(light, 1 - light);
    const f = n => light - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const toHex = x => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * A fresh five-colour palette that reads as one scheme rather than five
 * random hexes: a light tint and a mid tone on a base hue, a complementary
 * accent, a deep shade of the accent, and a near-neutral of the base. The
 * values are data (they are stored on the entity), not theme colours.
 */
export function randomPalette() {
    const h = Math.floor(Math.random() * 360);
    const spin = 150 + Math.floor(Math.random() * 60); // 150–210°: complement, loosely
    return [
        hslToHex(h, 55, 86),
        hslToHex((h + 25) % 360, 70, 58),
        hslToHex((h + spin) % 360, 65, 52),
        hslToHex((h + spin + 20) % 360, 55, 34),
        hslToHex(h, 25, 22),
    ];
}
